import { useEffect, useRef } from 'react'
import { useStore } from 'react-redux'
import type { RootState } from '@/store'
import { useAppDispatch } from '@/store/hooks'
import { api } from '@/store/api'
import {
  draftSourceSettled,
  setDraftProbe,
  setDraftSourceSettling,
} from '@/features/editor/editorSlice'
import { useGetCourseModerationQuery } from './moderationApi'
import { useCourseModeration } from './useCourseModeration'
import { useRefreshOnFocus } from '@/lib/useRefreshOnFocus'
import { isLiveModeration, pollingFor } from './lib/pollingFor'
import { isSourceBehind } from './lib/settlingWatchdog'
import { useFreshCourseModeration } from './useFreshCourseModeration'
import type { ReviewStatus } from './types'

/**
 * ЕДИНСТВЕННАЯ подписка на состояние модерации курса. Остановка — `pollingInterval: 0`,
 * а не `skip`: `skip` снял бы подписку и унёс терминальный ответ (вердикт, нарушения).
 * На переходе `pending → терминальный` курс перечитывается: видимость изменилась на бэке.
 * Упала ПЕРВАЯ загрузка (данных нет) — повтор раз в `MODERATION_ERROR_RETRY_MS`: иначе
 * лок не встанет до перезагрузки страницы. Пока запрос просто летит — повторов нет.
 * Живой курс вне проверки опрашивается раз в `DRAFT_POLL_MS` и на возврате во вкладку:
 * черновик мог завести MCP или вторая вкладка, и смена `draftId` идёт тем же путём, что
 * и после своей записи (инвалидация курса ниже → `useReadSourceRemap`).
 */
export const ModerationPoller = ({ courseId }: { courseId: number }) => {
  const dispatch = useAppDispatch()
  const store = useStore<RootState>()
  const { state } = useCourseModeration(courseId)
  // «Первая загрузка упала» — по СВЕЖЕМУ ответу: при устаревшем кеше упавший запрос на
  // монтировании иначе выглядел бы успешным (данные есть), и повтора не было бы.
  const fresh = useFreshCourseModeration(courseId)
  const status = state?.review?.status
  const isLive = isLiveModeration(state)
  const { refetch } = useGetCourseModerationQuery(courseId, {
    pollingInterval: pollingFor(status, fresh.isError && fresh.state === undefined, isLive),
    // Кеш прошлого открытия не в счёт: `draftId` мог смениться, пока редактор был закрыт.
    // Редактор ждёт именно этого ответа (`useFreshCourseModeration`).
    refetchOnMountOrArgChange: true,
  })
  // `refetch` СВОЕЙ подписки, а не второй подписчик: тот удвоил бы опрос.
  useRefreshOnFocus(isLive, refetch)

  // Два повода перечитать курс — и ОДНА инвалидация, даже если слияние принесло оба разом:
  // `pending → терминальный` (видимость изменилась на бэке) и смена `draftId` (источник
  // чтения редактора: `getEditorCourse` берёт `draftId` из этого кеша, но сам его не
  // слушает). Первый ответ по курсу не в счёт — курс ещё не читали, редактор ждёт его.
  // Списки блоков здесь НЕ сбрасываются: выделение ещё на уроках старого источника, и
  // удалённый черновик ответил бы 404 с тостом — их сбрасывает `useReadSourceRemap`,
  // уже зная уроки нового источника. Ключ по курсу: смена курса без перемонтирования не
  // должна давать ложного перехода.
  const draftId = state?.draftId
  type Seen = { courseId: number; status: ReviewStatus | undefined; draftId?: number | null }
  const prev = useRef<Seen | null>(null)
  useEffect(() => {
    const seen = prev.current?.courseId === courseId ? prev.current : null
    const settled = seen?.status === 'pending' && status !== undefined && status !== 'pending'
    const lastDraft = seen?.draftId
    const sourceChanged = draftId !== undefined && lastDraft !== undefined && lastDraft !== draftId
    // Сменился источник (черновик появился — в том числе пойманный тиком или фокусом, а не
    // зондом — или слит/отменён): до переезда пакетная запись собрала бы тела из кеша
    // ушедшего источника. Признак встаёт ДО инвалидации курса; снимает его переезд. Курс
    // уже перечитан из нового источника (успела отложенная инвалидация записи) — не ставим:
    // переезд по нему уже идёт, а признак ждал бы сторожа.
    if (sourceChanged && isSourceBehind(store.getState(), courseId))
      dispatch(setDraftSourceSettling(courseId))
    if (settled || sourceChanged) {
      const list = settled ? [{ type: 'Course', id: 'LIST' } as const] : []
      dispatch(api.util.invalidateTags([{ type: 'Course', id: courseId }, ...list]))
    }
    prev.current = { courseId, status, draftId: draftId === undefined ? lastDraft : draftId }
  }, [status, draftId, courseId, dispatch, store])

  // Зонд первого черновика: у живого курса без черновика первая правка заведёт его
  // на бэке, и `draftProbeMiddleware` перечитает `/moderation` после записи.
  const probe = state !== undefined && state.visibility !== 'unpublished' && state.draftId === null
  useEffect(() => {
    dispatch(setDraftProbe(probe ? courseId : null))
    return () => {
      dispatch(setDraftProbe(null))
    }
  }, [probe, courseId, dispatch])
  // Признак оседания источника снимается только на размонтировании (смене курса), а НЕ
  // со снятием зонда: черновик появился — зонд снят, но переезд на него ещё впереди.
  useEffect(
    () => () => {
      dispatch(draftSourceSettled(courseId))
    },
    [courseId, dispatch],
  )

  return null
}
